import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { useAppStore } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import { useRenderSettingsStore } from '../state/useRenderSettingsStore';
import { useSketchStore } from '../state/useSketchStore';
import { useSelectionStore } from '../tools/selection';
import { useViewStore } from '../state/commandAuxStores';

/**
 * Demand Render Loop Controller (F1 fix, task 1.1)
 *
 * With frameloop="demand" the canvas only renders when invalidated.
 * This controller centralizes invalidation for all state-driven changes:
 * - Any app store change (selection, hover, view settings, tool previews)
 * - Any BIM store change (elements, views, levels)
 * - Render settings changes (dpr, shadows, post-processing)
 * - Selection box drags
 * Plus a small burst of delayed invalidations covering async resources
 * (troika fonts, procedural environment) after mount.
 */
export function DemandRenderController() {
    const invalidate = useThree((state) => state.invalidate);

    useEffect(() => {
        // useViewStore carries the analysis/polar/iso display flags: toggling
        // them via commands (ZEBRA, POLAR, SECTIONPLANE, TURNTABLE...) must
        // repaint the canvas to show/hide their overlays.
        // useSketchStore is the Sketch workbench document: section plane
        // on/off, solid commits and display changes arrive ONLY through it,
        // and its scene-graph components render null on those updates —
        // without this subscription the demand loop never repainted them.
        const stores = [useAppStore, useBIMStore, useRenderSettingsStore, useSelectionStore, useSketchStore, useViewStore] as const;

        // Per-frame coalescing: a pointermove storm can write the store dozens
        // of times in one frame; the old path scheduled invalidate() plus two
        // burst timers per write (3 renders per move). demand-mode invalidate()
        // is frame-coalesced by R3F itself, so scheduling ONE invalidate per
        // frame is enough. A single throttled follow-up covers late async
        // commits (tool preview geometry mounting in effects, troika Text
        // syncing) without the 3x render multiplier.
        let invalidateScheduled = false;
        let followUpTimer = 0;

        const scheduleInvalidate = () => {
            if (invalidateScheduled) return;
            invalidateScheduled = true;
            requestAnimationFrame(() => {
                invalidateScheduled = false;
                invalidate();
                // One throttled follow-up frame per burst, not per store write.
                window.clearTimeout(followUpTimer);
                followUpTimer = window.setTimeout(() => invalidate(), 50);
            });
        };

        const unsubscribers = stores.map((store) => store.subscribe(() => scheduleInvalidate()));

        // REGEN (canonical command system): force a fresh render on demand.
        const handleRegen = () => scheduleInvalidate();
        window.addEventListener('yap:regen', handleRegen);

        // Live preview loop: while a drawing gesture is in progress (toolPreview.active),
        // render every frame so the rubber-band preview follows the cursor without
        // depending on camera-motion frames (OrbitControls damping is disabled).
        let previewRaf = 0;
        let lastPreviewInvalidateTime = 0;
        const MIN_FRAME_INTERVAL = 16; // ~60fps cap to prevent blocking
        
        const runPreviewLoop = (currentTime: number) => {
            // Throttle invalidations to avoid blocking requestAnimationFrame
            if (currentTime - lastPreviewInvalidateTime >= MIN_FRAME_INTERVAL) {
                invalidate();
                lastPreviewInvalidateTime = currentTime;
            }
            previewRaf = requestAnimationFrame(runPreviewLoop);
        };
        const unsubPreview = useAppStore.subscribe((state) => {
            const drawing = !!state.toolPreview?.active;
            if (drawing && !previewRaf) {
                lastPreviewInvalidateTime = performance.now();
                previewRaf = requestAnimationFrame(runPreviewLoop);
            } else if (!drawing && previewRaf) {
                cancelAnimationFrame(previewRaf);
                previewRaf = 0;
            }
        });

        const timers = [
            window.setTimeout(() => invalidate(), 250),
            window.setTimeout(() => invalidate(), 1000),
        ];

        return () => {
            unsubscribers.forEach((unsub) => unsub());
            window.removeEventListener('yap:regen', handleRegen);
            unsubPreview();
            window.clearTimeout(followUpTimer);
            if (previewRaf) cancelAnimationFrame(previewRaf);
            timers.forEach((timer) => window.clearTimeout(timer));
        };
    }, [invalidate]);

    return null;
}
